import type { ReactNode } from "react";
import { haptic, play } from "../lib/sfx";

/** On/off switch with a label. */
export function Toggle({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange(v: boolean): void; label: ReactNode; hint?: ReactNode; disabled?: boolean }) {
  return (
    <label className={`flex cursor-pointer items-center justify-between gap-4 py-2 ${disabled ? "opacity-50" : ""}`}>
      <span className="min-w-0">
        <span className="block font-bold">{label}</span>
        {hint && <span className="mt-0.5 block text-sm text-ink-3">{hint}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => {
          play("tap");
          haptic(6);
          onChange(!checked);
        }}
        className="relative h-8 w-14 shrink-0 rounded-full transition-colors duration-200"
        style={{ background: checked ? "var(--color-good)" : "var(--color-ground-4)" }}
      >
        <span
          className="absolute top-1 left-1 h-6 w-6 rounded-full bg-ink transition-transform duration-200"
          style={{ transform: `translateX(${checked ? 24 : 0}px)`, transitionTimingFunction: "var(--ease-spring)" }}
        />
      </button>
    </label>
  );
}

/** One-of-many choice rendered as pills. */
export function Segmented<T extends string | number>({
  value, options, onChange, disabled, color,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange(v: T): void;
  disabled?: boolean;
  color?: string;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={disabled}
          className="chip"
          style={color ? ({ "--c": color } as React.CSSProperties) : undefined}
          onClick={() => {
            play("tap");
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Many-of-many choice. An empty selection means "all". */
export function ChipSelect({
  values, options, onChange, disabled, allLabel,
}: {
  values: string[];
  options: { value: string; label: ReactNode }[];
  onChange(v: string[]): void;
  disabled?: boolean;
  allLabel: ReactNode;
}) {
  const all = values.length === 0;
  return (
    <div className="flex flex-wrap gap-2">
      <button type="button" className="chip" aria-pressed={all} disabled={disabled} onClick={() => onChange([])}>
        {allLabel}
      </button>
      {options.map((o) => {
        const on = values.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            className="chip"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => {
              play("tap");
              onChange(on ? values.filter((x) => x !== o.value) : [...values, o.value]);
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Field({ label, children, hint, error }: { label: ReactNode; children: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  return (
    <div>
      <span className="label">{label}</span>
      {children}
      {error ? <p className="mt-1.5 text-sm font-semibold text-bad">{error}</p> : hint ? <p className="mt-1.5 text-sm text-ink-3">{hint}</p> : null}
    </div>
  );
}

export function Section({ title, children, aside }: { title: ReactNode; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-lg font-extrabold">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}
